import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  DestroyRef,
  InjectionToken,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { environment } from '@/environments/environment';
import {
  analyticsConsentCookie,
  readAnalyticsConsent,
} from './analytics-consent-cookie';

export interface AnalyticsConfig {
  enabled: boolean;
  measurementId: string;
  hostname: string;
}

export const ANALYTICS_CONFIG = new InjectionToken<AnalyticsConfig>(
  'Analytics config',
  {
    providedIn: 'root',
    factory: () => environment.analytics,
  },
);

export const ANALYTICS_CONSENT_KEY = 'reazly.analytics-consent.v1';
type Consent = 'accepted' | 'denied' | 'pending';
type Page = {
  page_location: string;
  page_title: string;
  page_referrer: string;
};
type TagCommand =
  | ['js', Date]
  | ['consent', 'default' | 'update', {
      analytics_storage: 'granted' | 'denied';
      ad_storage: 'denied';
      ad_user_data: 'denied';
      ad_personalization: 'denied';
    }]
  | [
      'config',
      string,
      Page & {
        send_page_view: false;
        cookie_domain: string;
        allow_google_signals: false;
        allow_ad_personalization_signals: false;
      },
    ]
  | ['set', Page]
  | ['event', 'page_view', Page & { send_to: string }];
type GoogleTag = (...args: TagCommand) => void;
type AnalyticsWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: GoogleTag;
} & Partial<Record<`ga-disable-${string}`, boolean>>;

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = inject(ANALYTICS_CONFIG);
  private readonly router = inject(Router);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly window = this.document.defaultView as AnalyticsWindow | null;
  private readonly choice = signal<Consent>('pending');
  readonly consent = this.choice.asReadonly();
  readonly enabled =
    this.browser &&
    this.config.enabled &&
    this.window?.location.hostname === this.config.hostname &&
    this.window.location.protocol === 'https:' &&
    /^G-[A-Z0-9]+$/.test(this.config.measurementId);
  private initialized = false;
  private started = false;
  private failed = false;
  private previousLocation = '';

  initialize(): void {
    if (this.initialized || !this.enabled) return;
    this.initialized = true;
    // v1 localStorage decisions covered only the app: ask again for site + app.
    this.choice.set(this.readChoice());
    const sync = () => {
      if (this.document.visibilityState !== 'hidden') this.synchronize();
    };
    this.window?.addEventListener('focus', sync);
    this.document.addEventListener('visibilitychange', sync);
    const timer = this.window?.setInterval(sync, 2000);
    this.destroyRef.onDestroy(() => {
      this.window?.removeEventListener('focus', sync);
      this.document.removeEventListener('visibilitychange', sync);
      this.window?.clearInterval(timer);
    });
    if (this.choice() !== 'pending' && this.router.navigated) this.trackPage();
  }

  accept(): void {
    if (!this.enabled || this.choice() === 'accepted') return;
    this.persist('accepted');
    if (this.started) this.updateConsent('accepted');
    else if (this.router.navigated) this.trackPage();
  }

  decline(): void {
    if (!this.enabled) return;
    this.persist('denied');
    if (this.started) this.stop();
    else if (this.router.navigated) this.trackPage();
  }

  private stop(): void {
    if (!this.started || !this.window) return;
    this.window[`ga-disable-${this.config.measurementId}`] = true;
    this.window.gtag = () => undefined;
    if (this.window.dataLayer) this.window.dataLayer.length = 0;
    this.document.getElementById('reazly-google-analytics')?.remove();
    try {
      this.clearCookies();
    } catch {
      // Restricted cookie access must not prevent unloading the tag.
    }
    this.reload();
  }

  trackPage(): void {
    if (this.initialized) this.synchronize(false);
    if (
      !this.enabled ||
      this.choice() === 'pending' ||
      !this.window ||
      this.failed
    )
      return;
    const page = this.currentPage();
    if (!this.started) {
      this.started = true;
      const layer: IArguments[] = [];
      this.window.dataLayer = layer;
      this.window.gtag = function (..._args: TagCommand): void {
        // Bound the queue while a slow or blocked script is loading.
        if (layer.length >= 100) layer.splice(3, 2);
        layer.push(arguments);
      };
      this.window.gtag('consent', 'default', this.consentSettings('denied'));
      if (this.choice() === 'accepted') this.updateConsent('accepted');
      this.window.gtag('js', new Date());
      this.window.gtag('set', page);
      this.window.gtag('config', this.config.measurementId, {
        ...page,
        send_page_view: false,
        cookie_domain: this.config.hostname,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      const script = this.document.createElement('script');
      script.id = 'reazly-google-analytics';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
      // A blocked tag must never interrupt navigation or trigger retry loops.
      script.onerror = () => {
        this.failed = true;
        if (this.window?.dataLayer) this.window.dataLayer.length = 0;
      };
      this.document.head.appendChild(script);
    }
    this.window.gtag?.('set', page);
    this.window.gtag?.('event', 'page_view', {
      ...page,
      send_to: this.config.measurementId,
    });
    this.previousLocation = page.page_location;
  }

  private consentSettings(choice: 'accepted' | 'denied') {
    return {
      analytics_storage: choice === 'accepted' ? 'granted' as const : 'denied' as const,
      ad_storage: 'denied' as const,
      ad_user_data: 'denied' as const,
      ad_personalization: 'denied' as const,
    };
  }

  private updateConsent(choice: 'accepted' | 'denied'): void {
    this.window?.gtag?.('consent', 'update', this.consentSettings(choice));
  }

  private persist(choice: 'accepted' | 'denied'): void {
    this.choice.set(choice);
    try {
      this.document.cookie = analyticsConsentCookie(choice);
    } catch {
      /* Keep the choice in memory for this visit. */
    }
  }

  private readChoice(): Consent {
    try {
      return readAnalyticsConsent(this.document.cookie);
    } catch {
      return 'pending';
    }
  }

  private synchronize(track = true): void {
    if (!this.enabled) return;
    const next = this.readChoice();
    if (next === this.choice()) return;
    this.choice.set(next);
    if (this.started && next !== 'accepted') this.stop();
    else if (this.started && next === 'accepted') this.updateConsent('accepted');
    else if (next !== 'pending' && track && this.router.navigated) this.trackPage();
  }

  private currentPage(): Page {
    const parts: string[] = [];
    let route: ActivatedRouteSnapshot | undefined =
      this.router.routerState.snapshot.root;
    let title = 'Reazly';
    let unknown = false;
    while (route) {
      const config = route.routeConfig;
      if (config?.matcher) parts.push(':publicId', ':slug');
      else if (config?.path === '**') unknown = true;
      else if (config?.path) parts.push(config.path);
      const routeTitle: unknown = config?.data?.['title'];
      if (typeof routeTitle === 'string') title = `${routeTitle} | Reazly`;
      route = route.firstChild ?? undefined;
    }
    const path =
      unknown || parts.length === 0 ? '/unknown' : `/${parts.join('/')}`;
    return {
      page_location: `https://${this.config.hostname}${path}`,
      page_title: title,
      // Never use document.referrer: it can contain login or sharing tokens.
      page_referrer: this.previousLocation,
    };
  }

  private clearCookies(): void {
    // App cookies are explicitly scoped to this host; preserve WordPress cookies.
    const domains = ['', this.config.hostname, `.${this.config.hostname}`];
    for (const cookie of this.document.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!/^(_ga(?:_|$)|_gid$|_gat(?:_|$))/.test(name)) continue;
      for (const domain of domains) {
        this.document.cookie = `${name}=; Max-Age=0; Path=/;${domain ? ` Domain=${domain};` : ''} SameSite=Lax; Secure`;
      }
    }
  }

  protected reload(): void {
    this.window?.location.reload();
  }
}
